"use client";
import {
  Children,
  cloneElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  useRef,
  type FC,
  type Ref,
} from "react";
import { AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { useForm } from "../../hooks/useForm";
import { useUuid } from "../../hooks/useUuid";
import { mergeRefs } from "../../utils/mergeRefs";
import { FormLabel } from "../FormLabel";
import { Tooltip } from "../Tooltip";
import { Icon } from "../Icon";
import { FormInputError } from "../FormInputError";
import { Text } from "../Text";

import {
  formRowStyle,
  inputContainerStyle,
  labelStyle,
  requiredStyle,
  helpIconStyle,
} from "./FormRow.css";

export type FormRowProps = HTMLAttributes<HTMLDivElement> &
  RecipeVariants<typeof formRowStyle> & {
    label?: ReactNode;
    description?: ReactNode;
    emptyLabel?: ReactNode;
    required?: boolean;
    help?: ReactNode;
    error?: string;
    "data-field-key"?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A form row component that contains a label and a form element.
 */
export const FormRow: FC<FormRowProps> = ({
  description,
  variant = "auto",
  label,
  emptyLabel = false,
  required = false,
  help,
  error,
  grow = false,
  children,
  "data-field-key": dataFieldKey,
  ref,
  ...props
}) => {
  const id = useUuid();
  const { t } = useTranslation();
  const internalRef = useRef<HTMLDivElement>(null);

  const form = useForm();

  const childrenArray = Children.toArray(children);
  const firstChild = childrenArray?.[0] as ReactElement<any>;

  const errorMessage: string | null = form?.errors?.[id]?.[0] ?? null;

  return (
    <div
      ref={mergeRefs(internalRef, ref)}
      className={formRowStyle({ variant, grow })}
      {...props}
    >
      {label && (
        <FormLabel
          className={labelStyle({ variant })}
          htmlFor={id}
          ellipsis={false}
          leading={
            help && (
              <Tooltip value={help}>
                <Icon className={helpIconStyle} icon="help" size="s1" />
              </Tooltip>
            )
          }
          trailing={required && <span className={requiredStyle}>*</span>}
        >
          {label}
        </FormLabel>
      )}

      {emptyLabel && !label && variant !== "vertical" && (
        <div className={labelStyle({ empty: true, variant })} />
      )}

      <div
        className={inputContainerStyle({
          hasDescriptionAuto: !!description && variant === "auto",
          variant,
        })}
      >
        {description && (
          <Text markdown variant="bodyS" color="currentColor50" italic>
            {description}
          </Text>
        )}

        {firstChild &&
          cloneElement(firstChild, {
            ref: mergeRefs(internalRef),
            id,
            ...(dataFieldKey ? { fieldKey: dataFieldKey } : {}),
          })}
        {childrenArray.slice(1)}

        <AnimatePresence>
          {(error || errorMessage) && (
            <FormInputError
              message={
                error ? error : errorMessage ? t(errorMessage) : undefined
              }
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
